/**
 * COACHDIA Pulse — Owner Flow (Iter253 Phase 3)
 * ==============================================
 *
 * Produit B2B Pulse indépendant de Discovery. 4 écrans séquentiels :
 *   1. INVITE     — saisie emails + envoi d'invitations (aucun prix affiché)
 *   2. DASHBOARD  — suivi participants + relance + CTA "Générer" (≥ 2)
 *   3. TEASER     — aperçu flouté + 1ère exposition prix + CTA "Débloquer"
 *   4. REPORT     — rapport complet + téléchargement PDF
 *
 * Règles strictes (user Iter253) :
 *   ❌ Zéro fallback Discovery
 *   ❌ Zéro prix avant Écran 3 (teaser)
 *   ❌ Zéro message technique visible
 *   ✅ UX premium (palette espresso #1F1A12 / crème #F5F1EA / or #92710A)
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Users, Send, Mail, Loader2, Plus, X, Check, Clock,
  ArrowRight, RefreshCw, Lock, Sparkles, AlertCircle, ChevronLeft,
  Home, BarChart3, FileText, Download, ShieldCheck,
} from 'lucide-react';
import { getApiUrl } from '../config/environment';
import { useAuth } from '../contexts/AuthContext';
import BackButton from '../components/BackButton';

const API = getApiUrl();

const authHeaders = () => {
  const t = localStorage.getItem('token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

// Filtre anti-fuite technique — retourne un message FR si le backend renvoie
// un détail brut anglais ou inconnu (règle user : zéro message technique).
const friendlyError = (err, fallback) => {
  const raw = err?.response?.data?.detail;
  if (typeof raw !== 'string' || !raw) return fallback;
  const lower = raw.toLowerCase();
  // Blacklist messages techniques EN ou middleware
  const techBlacklist = [
    'not found', 'internal server', 'bad request', 'unauthorized',
    'forbidden', 'unprocessable', 'rate limit', 'gateway', 'timeout',
    'service unavailable', 'validation error',
  ];
  if (techBlacklist.some((s) => lower.includes(s))) return fallback;
  // Whitelist : accent FR OU mot-clé métier FR
  const hasAccent = /[éèêàâîôûçùœ]/i.test(raw);
  const frKeywords = /\b(profil|équipe|rapport|envoi|seuil|invitation|relance|batch|participant|complété|expiré|participant·e)\b/i;
  if (hasAccent || frKeywords.test(raw)) return raw;
  return fallback;
};

const COLORS = {
  red:    { hex: '#C0392B', label: 'Action' },
  yellow: { hex: '#D4A017', label: 'Inspiration' },
  green:  { hex: '#2D8659', label: 'Harmonie' },
  blue:   { hex: '#1E4F8C', label: 'Analyse' },
};

// =============================================================================
// Email validation (RFC light)
// =============================================================================
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** 🆕 Iter263 — Parser bulk paste : détecte "Alice Durand <a@x.fr>" ou "Alice Durand, a@x.fr".
 *  Retourne { full_name, email } | null.
 */
const parseParticipantLine = (line) => {
  const raw = (line || '').trim();
  if (!raw) return null;
  // Format RFC: "Alice Durand <alice@x.fr>"
  let m = raw.match(/^(.*?)\s*<\s*([^>\s]+)\s*>$/);
  if (m) {
    const name = m[1].trim().replace(/^["']|["']$/g, '');
    const email = m[2].toLowerCase();
    if (EMAIL_RE.test(email)) return { full_name: name || null, email };
  }
  // Format CSV: "Alice Durand, alice@x.fr"  ou  "Alice Durand;alice@x.fr" ou "Alice Durand  alice@x.fr"
  m = raw.match(/^(.*?)[,;\t]\s*([^\s,;]+)$/);
  if (m) {
    const name = m[1].trim();
    const email = m[2].toLowerCase();
    if (EMAIL_RE.test(email)) return { full_name: name || null, email };
  }
  // Email seul — name absent (fallback vers autre tentative : "Nom Prénom email@x.fr" séparé par spaces)
  const tokens = raw.split(/\s+/);
  const lastTok = tokens[tokens.length - 1].toLowerCase();
  if (EMAIL_RE.test(lastTok)) {
    const name = tokens.slice(0, -1).join(' ').trim();
    return { full_name: name || null, email: lastTok };
  }
  return null;
};

// =============================================================================
// PHASE A — INVITATION
// =============================================================================
const InviteScreen = ({ onBatchCreated }) => {
  const [batchName, setBatchName] = useState('');
  const [inputName, setInputName] = useState('');
  const [inputEmail, setInputEmail] = useState('');
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkText, setBulkText] = useState('');
  const [participants, setParticipants] = useState([]); // [{full_name, email}]
  const [sending, setSending] = useState(false);

  const addParticipant = useCallback(() => {
    const email = inputEmail.trim().toLowerCase();
    const name = inputName.trim();
    if (!email) {
      toast.error('Email requis');
      return;
    }
    if (!EMAIL_RE.test(email)) {
      toast.error('Adresse email invalide');
      return;
    }
    if (!name || name.length < 2) {
      toast.error('Merci d\'ajouter le nom complet pour une restitution professionnelle du rapport.');
      return;
    }
    if (participants.some((p) => p.email === email)) {
      toast.message('Participant déjà ajouté');
      return;
    }
    setParticipants([...participants, { full_name: name, email }]);
    setInputName('');
    setInputEmail('');
  }, [inputName, inputEmail, participants]);

  const removeParticipant = (email) =>
    setParticipants(participants.filter((p) => p.email !== email));

  const handleKey = (ev) => {
    if (ev.key === 'Enter') {
      ev.preventDefault();
      addParticipant();
    }
  };

  const handleBulkImport = () => {
    const lines = bulkText.split(/\r?\n/);
    const parsed = [];
    const missingName = [];
    const skipped = [];
    const seen = new Set(participants.map((p) => p.email));
    for (const line of lines) {
      if (!line.trim()) continue;
      const p = parseParticipantLine(line);
      if (!p) {
        skipped.push(line.trim().slice(0, 40));
        continue;
      }
      if (seen.has(p.email)) continue;
      if (!p.full_name || p.full_name.length < 2) {
        missingName.push(p.email);
        continue;
      }
      parsed.push(p);
      seen.add(p.email);
    }
    if (missingName.length > 0) {
      toast.error(
        `Nom manquant pour ${missingName.length} ligne(s). Format attendu : "Alice Durand <alice@x.fr>" ou "Alice Durand, alice@x.fr"`,
      );
      return;
    }
    if (skipped.length > 0) {
      toast.message(`${skipped.length} ligne(s) ignorée(s) (format invalide)`);
    }
    if (parsed.length === 0) {
      toast.error('Aucun participant valide dans le bloc collé');
      return;
    }
    setParticipants([...participants, ...parsed]);
    setBulkText('');
    setBulkMode(false);
    toast.success(`${parsed.length} participant(s) ajouté(s) depuis le bloc`);
  };

  const handleSend = async () => {
    if (participants.length < 2) {
      toast.error('Minimum 2 participants');
      return;
    }
    setSending(true);
    try {
      const { data } = await axios.post(
        `${API}/team-pulse/batches`,
        {
          invited_participants: participants,
          batch_name: batchName.trim() || undefined,
        },
        { headers: authHeaders() },
      );
      toast.success(`${participants.length} invitation(s) envoyée(s).`);
      onBatchCreated(data.batch.id);
    } catch (err) {
      toast.error(friendlyError(err, 'Envoi impossible pour le moment.'));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-[#F5F1EA]" data-testid="pulse-invite-screen">
      <div className="max-w-4xl mx-auto px-6 py-12 md:py-20">
        {/* Eyebrow premium */}
        <div className="mb-10">
          <div className="h-px w-16 bg-gradient-to-r from-[#92710A] to-[#D4AF37] mb-6" />
          <div className="text-[11px] tracking-[0.28em] uppercase text-[#92710A] font-medium flex items-center gap-2">
            <Users className="h-3.5 w-3.5" /> COACHDIA Pulse · Nouvelle campagne
          </div>
          <h1 className="mt-3 text-4xl md:text-5xl lg:text-6xl font-serif leading-[1.05] text-[#1F1A12]">
            Invitez votre équipe
            <span className="block italic text-[#92710A] mt-2">à révéler sa dynamique.</span>
          </h1>
          <p className="mt-6 text-base text-[#4A3F2F] max-w-xl leading-relaxed">
            Chaque participant reçoit un lien personnel. Ses réponses alimentent
            une cartographie d'équipe — lisible par vous seul·e.
          </p>
        </div>

        {/* Formulaire */}
        <div className="bg-white border border-[#EDE4D0] rounded-2xl p-6 md:p-10 shadow-sm">
          <label className="block mb-8">
            <span className="text-xs uppercase tracking-[0.2em] text-[#8A7A5E] font-medium">
              Nom de la campagne · optionnel
            </span>
            <input
              type="text"
              value={batchName}
              onChange={(e) => setBatchName(e.target.value)}
              placeholder={`Équipe ${new Date().toLocaleDateString('fr-FR')}`}
              className="mt-2 w-full px-4 py-3 bg-[#FAF6EE] border border-[#EDE4D0] rounded-xl
                         focus:outline-none focus:border-[#92710A] text-[#1F1A12]"
              data-testid="pulse-batch-name-input"
              maxLength={80}
            />
          </label>

          <label className="block">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs uppercase tracking-[0.2em] text-[#8A7A5E] font-medium flex items-center gap-2">
                <Mail className="h-3.5 w-3.5" /> Participants ·
                <span className="text-[#1F1A12]" data-testid="pulse-emails-count">
                  {participants.length} ajouté{participants.length > 1 ? 's' : ''}
                </span>
              </span>
              <button
                type="button"
                onClick={() => setBulkMode(!bulkMode)}
                className="text-[10px] uppercase tracking-[0.22em] text-[#92710A] hover:text-[#1F1A12] transition-colors font-medium"
                data-testid="pulse-bulk-toggle"
              >
                {bulkMode ? '← Saisie unitaire' : 'Import en masse →'}
              </button>
            </div>

            {!bulkMode ? (
              <div className="mt-1">
                <div className="flex flex-col md:flex-row gap-2">
                  <input
                    type="text"
                    value={inputName}
                    onChange={(e) => setInputName(e.target.value)}
                    onKeyDown={handleKey}
                    placeholder="Nom complet"
                    className="md:w-2/5 px-4 py-3 bg-[#FAF6EE] border border-[#EDE4D0] rounded-xl
                               focus:outline-none focus:border-[#92710A] text-[#1F1A12]"
                    data-testid="pulse-name-input"
                    maxLength={80}
                  />
                  <input
                    type="email"
                    value={inputEmail}
                    onChange={(e) => setInputEmail(e.target.value)}
                    onKeyDown={handleKey}
                    placeholder="prenom@entreprise.com"
                    className="flex-1 px-4 py-3 bg-[#FAF6EE] border border-[#EDE4D0] rounded-xl
                               focus:outline-none focus:border-[#92710A] text-[#1F1A12]"
                    data-testid="pulse-email-input"
                  />
                  <button
                    onClick={addParticipant}
                    className="px-5 py-3 bg-[#1F1A12] text-white rounded-xl hover:bg-[#2A2319]
                               transition-colors flex items-center gap-2 text-sm font-medium"
                    data-testid="pulse-add-email-btn"
                  >
                    <Plus className="h-4 w-4" /> Ajouter
                  </button>
                </div>
                <p className="mt-2 text-xs text-[#8A7A5E]">
                  Nom complet requis — le rapport affichera l'identité lisible, jamais l'email.
                </p>
              </div>
            ) : (
              <div className="mt-1 space-y-3">
                <textarea
                  value={bulkText}
                  onChange={(e) => setBulkText(e.target.value)}
                  rows={6}
                  placeholder={'Alice Durand <alice@corp.fr>\nBob Martin, bob@corp.fr\nCarole Leroy caroline@corp.fr'}
                  className="w-full px-4 py-3 bg-[#FAF6EE] border border-[#EDE4D0] rounded-xl
                             focus:outline-none focus:border-[#92710A] text-[#1F1A12] font-mono text-sm"
                  data-testid="pulse-bulk-textarea"
                />
                <div className="flex flex-wrap gap-2 items-center justify-between">
                  <p className="text-xs text-[#8A7A5E]">
                    Formats reconnus : <code className="bg-[#FAF6EE] px-1.5 py-0.5 rounded">Nom &lt;email&gt;</code>,
                    <code className="bg-[#FAF6EE] px-1.5 py-0.5 rounded ml-1">Nom, email</code> ou
                    <code className="bg-[#FAF6EE] px-1.5 py-0.5 rounded ml-1">Nom Prénom email</code>
                  </p>
                  <button
                    type="button"
                    onClick={handleBulkImport}
                    disabled={!bulkText.trim()}
                    className="px-4 py-2 bg-[#92710A] text-white rounded-lg hover:bg-[#7A5E08]
                               transition-colors flex items-center gap-2 text-sm font-medium
                               disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="pulse-bulk-import-btn"
                  >
                    Importer la liste
                  </button>
                </div>
              </div>
            )}
          </label>

          {/* Chips participants — Nom · email */}
          {participants.length > 0 && (
            <div className="mt-6 flex flex-wrap gap-2" data-testid="pulse-emails-list">
              {participants.map((p) => (
                <span
                  key={p.email}
                  className="inline-flex items-center gap-2 bg-[#FAF6EE] border border-[#EDE4D0]
                             rounded-full px-3 py-1.5 text-sm"
                  data-testid={`pulse-participant-chip-${p.email}`}
                >
                  <span className="text-[#1F1A12] font-medium">{p.full_name}</span>
                  <span className="text-[#8A7A5E] text-xs">· {p.email}</span>
                  <button
                    onClick={() => removeParticipant(p.email)}
                    className="text-[#8A7A5E] hover:text-[#C0392B]"
                    data-testid={`pulse-email-remove-${p.email}`}
                    aria-label={`Retirer ${p.full_name}`}
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </span>
              ))}
            </div>
          )}

          {/* CTA principal */}
          <div className="mt-10 flex items-center justify-between gap-4 border-t border-[#EDE4D0] pt-6">
            <div className="text-xs text-[#8A7A5E]">
              Minimum 2 participants · expiration automatique dans 7 jours
            </div>
            <button
              onClick={handleSend}
              disabled={sending || participants.length < 2}
              className="inline-flex items-center gap-2 bg-[#1F1A12] text-white px-7 py-3.5
                         rounded-full font-medium text-sm tracking-wide hover:bg-[#2A2319]
                         transition disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="pulse-send-invitations-btn"
            >
              {sending ? (
                <><Loader2 className="h-4 w-4 animate-spin" /> Envoi…</>
              ) : (
                <>Envoyer les invitations <Send className="h-4 w-4" /></>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// PHASE B — DASHBOARD PROGRESSION
// =============================================================================
const InvitationRow = ({ inv, onRemind, reminding }) => {
  const statusBadge = {
    not_started: { label: 'Non commencé', cls: 'bg-stone-100 text-stone-600', icon: Clock },
    in_progress: { label: 'En cours', cls: 'bg-amber-50 text-amber-800 border-amber-200', icon: Loader2 },
    completed:   { label: 'Terminé', cls: 'bg-emerald-50 text-emerald-800 border-emerald-200', icon: Check },
    expired:     { label: 'Expiré', cls: 'bg-stone-100 text-stone-500', icon: AlertCircle },
  }[inv.status] || { label: inv.status, cls: 'bg-stone-100', icon: Clock };
  const Icon = statusBadge.icon;
  const canRemind = inv.status !== 'completed' && inv.status !== 'expired';

  return (
    <div
      className="flex items-center justify-between py-4 border-b border-[#EDE4D0] last:border-0"
      data-testid={`pulse-participant-row-${inv.email}`}
    >
      <div className="flex items-center gap-3 min-w-0">
        <div className="h-9 w-9 rounded-full bg-[#FAF6EE] border border-[#EDE4D0] flex items-center justify-center text-xs font-semibold text-[#92710A]">
          {(inv.email || '?')[0].toUpperCase()}
        </div>
        <div className="min-w-0">
          <div className="text-sm text-[#1F1A12] font-medium truncate">{inv.email}</div>
          <div className="text-xs text-[#8A7A5E] mt-0.5">
            {inv.email_opened_at && <span>Ouvert · </span>}
            {inv.reminder_count > 0 && (
              <span data-testid={`pulse-reminder-count-${inv.email}`}>
                {inv.reminder_count} relance{inv.reminder_count > 1 ? 's' : ''}
              </span>
            )}
            {!inv.email_opened_at && !inv.reminder_count && <span>Invitation envoyée</span>}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-3 shrink-0">
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs border ${statusBadge.cls}`}>
          <Icon className="h-3 w-3" /> {statusBadge.label}
        </span>
        {canRemind && (
          <button
            onClick={() => onRemind(inv.id)}
            disabled={reminding === inv.id}
            className="text-xs text-[#92710A] hover:text-[#1F1A12] font-medium inline-flex items-center gap-1.5 disabled:opacity-50"
            data-testid={`pulse-remind-btn-${inv.email}`}
          >
            {reminding === inv.id ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <RefreshCw className="h-3 w-3" />
            )}
            Relancer
          </button>
        )}
      </div>
    </div>
  );
};

const DashboardScreen = ({ batch, invitations, progress, onRefresh, onGenerate, generating, onNew, onViewTeaser, onViewReport }) => {
  const [reminding, setReminding] = useState(null);

  const handleRemind = async (invId) => {
    setReminding(invId);
    try {
      await axios.post(
        `${API}/team-pulse/batches/${batch.id}/remind`,
        { invitation_id: invId },
        { headers: authHeaders() },
      );
      toast.success('Relance envoyée.');
      await onRefresh();
    } catch (err) {
      toast.error(friendlyError(err, 'Relance impossible pour le moment.'));
    } finally {
      setReminding(null);
    }
  };

  const pct = progress?.completion_pct || 0;
  // 🎯 Iter257 A2 — CTA piloté par progress.report_action (3 états)
  const action = progress?.report_action;
  const ctaConfig = (() => {
    if (action === 'view_report') {
      return {
        label: 'Voir le rapport',
        handler: onViewReport,
        enabled: !generating,
        heading: 'Rapport disponible.',
        subtext: 'Votre cartographie d\'équipe est prête — consultez-la en un clic.',
        testid: 'pulse-view-report-btn',
      };
    }
    if (action === 'view_teaser') {
      return {
        label: 'Débloquer le rapport',
        handler: onViewTeaser,
        enabled: !generating,
        heading: 'Rapport généré — prêt à être révélé.',
        subtext: 'Il ne reste qu\'une étape pour accéder à la cartographie complète de votre équipe.',
        testid: 'pulse-view-teaser-btn',
      };
    }
    // Default: generate
    return {
      label: 'Générer le rapport',
      handler: onGenerate,
      enabled: action === 'generate' && !generating,
      heading: action === 'generate'
        ? 'Prêt à être généré.'
        : 'Patientez, il manque encore quelques profils.',
      subtext: action === 'generate'
        ? `${progress?.completed} profils exploitables suffisent pour cartographier la dynamique d'équipe.`
        : `Minimum ${progress?.min_required || 2} participants complétés — actuellement ${progress?.completed}.`,
      testid: 'pulse-generate-report-btn',
    };
  })();

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-[#F5F1EA]" data-testid="pulse-dashboard-screen">
      <div className="max-w-5xl mx-auto px-6 py-10 md:py-14">
        <div className="flex items-start justify-between gap-4 mb-8">
          <div className="min-w-0">
            <div className="h-px w-16 bg-gradient-to-r from-[#92710A] to-[#D4AF37] mb-5" />
            <div className="text-[11px] tracking-[0.28em] uppercase text-[#92710A] font-medium">
              COACHDIA Pulse · Progression
            </div>
            <h1 className="mt-2 text-3xl md:text-4xl font-serif text-[#1F1A12] leading-tight" data-testid="pulse-batch-name">
              {batch.name}
            </h1>
            <p className="mt-2 text-sm text-[#8A7A5E]">
              {progress?.days_remaining} jour{progress?.days_remaining > 1 ? 's' : ''} restant{progress?.days_remaining > 1 ? 's' : ''} · expire le {new Date(batch.expires_at).toLocaleDateString('fr-FR')}
            </p>
          </div>
          <button
            onClick={onNew}
            className="text-xs text-[#92710A] hover:text-[#1F1A12] inline-flex items-center gap-1.5 shrink-0 mt-2"
            data-testid="pulse-new-campaign-btn"
          >
            <Plus className="h-3.5 w-3.5" /> Nouvelle campagne
          </button>
        </div>

        {/* Stats triptyque */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="bg-white border border-[#EDE4D0] rounded-xl p-5">
            <div className="text-xs uppercase tracking-[0.2em] text-[#8A7A5E]">Terminés</div>
            <div className="mt-1 text-3xl font-serif text-[#1F1A12] tabular-nums" data-testid="pulse-stat-completed">
              {progress?.completed}<span className="text-lg text-[#8A7A5E]">/{progress?.total}</span>
            </div>
          </div>
          <div className="bg-white border border-[#EDE4D0] rounded-xl p-5">
            <div className="text-xs uppercase tracking-[0.2em] text-[#8A7A5E]">En cours</div>
            <div className="mt-1 text-3xl font-serif text-[#1F1A12] tabular-nums">
              {progress?.in_progress}
            </div>
          </div>
          <div className="bg-white border border-[#EDE4D0] rounded-xl p-5">
            <div className="text-xs uppercase tracking-[0.2em] text-[#8A7A5E]">Non commencés</div>
            <div className="mt-1 text-3xl font-serif text-[#1F1A12] tabular-nums">
              {progress?.not_started}
            </div>
          </div>
        </div>

        {/* Progress bar */}
        <div className="mb-10">
          <div className="flex items-center justify-between text-xs text-[#8A7A5E] mb-2">
            <span>Complétion</span>
            <span className="tabular-nums">{pct.toFixed(0)}%</span>
          </div>
          <div className="h-2 bg-[#EDE4D0] rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-[#92710A] to-[#D4AF37] transition-all"
              style={{ width: `${Math.min(100, pct)}%` }}
              data-testid="pulse-progress-bar"
            />
          </div>
        </div>

        {/* Liste participants */}
        <div className="bg-white border border-[#EDE4D0] rounded-2xl p-6 md:p-8">
          <div className="text-xs uppercase tracking-[0.2em] text-[#8A7A5E] mb-4 flex items-center justify-between">
            <span>Participants</span>
            <button
              onClick={onRefresh}
              className="text-[#92710A] hover:text-[#1F1A12] inline-flex items-center gap-1.5"
              data-testid="pulse-refresh-btn"
            >
              <RefreshCw className="h-3 w-3" /> Actualiser
            </button>
          </div>
          <div>
            {invitations.map((inv) => (
              <InvitationRow
                key={inv.id}
                inv={inv}
                onRemind={handleRemind}
                reminding={reminding}
              />
            ))}
          </div>
        </div>

        {/* Generate CTA — pilote par progress.report_action (Iter257 A2/B3) */}
        <div className="mt-10 bg-[#1F1A12] text-white rounded-2xl p-8 md:p-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="min-w-0">
            <div className="text-[11px] tracking-[0.28em] uppercase text-[#D4AF37] font-medium">
              Rapport équipe
            </div>
            <h3 className="mt-2 text-2xl font-serif leading-tight">
              {ctaConfig.heading}
            </h3>
            <p className="mt-2 text-sm text-white/70">
              {ctaConfig.subtext}
            </p>
          </div>
          <button
            onClick={ctaConfig.handler}
            disabled={!ctaConfig.enabled}
            className="inline-flex items-center gap-2 bg-white text-[#1F1A12] px-7 py-3.5 rounded-full font-medium text-sm hover:bg-[#F5F1EA] transition disabled:opacity-30 disabled:cursor-not-allowed shrink-0 whitespace-nowrap"
            data-testid={ctaConfig.testid}
          >
            {generating ? (
              <><Loader2 className="h-4 w-4 animate-spin" /> Génération…</>
            ) : (
              <>{ctaConfig.label} <ArrowRight className="h-4 w-4" /></>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// PHASE C — TEASER PREMIUM (Iter257 C2 — révélation + conversion)
// =============================================================================
const TeaserScreen = ({ batch, report, onUnlock, unlocking, pack, onBackToDashboard, onManualReconcile }) => {
  const teamSize = report?.team_size || batch?.report_completed_count || 0;
  const dominant = report?.team_dominant || 'red';
  const colorMeta = COLORS[dominant] || COLORS.red;
  const distribution = report?.distribution || {};
  const priceEur = pack?.effective_price_eur;
  const basePrice = pack?.base_price_eur;
  const promoActive = pack?.promo_effective;

  // Blocs teasers (compteurs non-flous, contenu flouté)
  const counts = {
    synergies: Math.max(2, Math.round(teamSize * 1.5)),
    frictions: Math.max(1, Math.round(teamSize * 0.6)),
    blind_spots: 3,
    scripts: 4,
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-[#F5F1EA] pb-32 md:pb-16" data-testid="pulse-teaser-screen">
      <div className="max-w-5xl mx-auto px-6 py-10 md:py-16">
        {/* Nav retour dashboard (accessible même depuis auto-load teaser) */}
        {onBackToDashboard && (
          <button
            onClick={onBackToDashboard}
            className="mb-6 text-xs text-[#92710A] hover:text-[#1F1A12] inline-flex items-center gap-1.5"
            data-testid="pulse-teaser-back-dashboard-btn"
          >
            <ChevronLeft className="h-3.5 w-3.5" /> Voir le tableau de bord
          </button>
        )}
        {/* ──── HERO — ADN révélé partiellement ──── */}
        <div className="mb-10">
          <div className="h-px w-16 bg-gradient-to-r from-[#92710A] to-[#D4AF37] mb-5" />
          <div className="text-[11px] tracking-[0.28em] uppercase text-[#92710A] font-medium inline-flex items-center gap-2">
            <Sparkles className="h-3.5 w-3.5" /> Rapport prêt · {teamSize} profils analysés
          </div>
          <h1 className="mt-3 text-4xl md:text-6xl font-serif leading-[1.02] text-[#1F1A12]">
            Votre équipe a un
            <span className="block italic text-[#92710A]">ADN {colorMeta.label.toLowerCase()}.</span>
          </h1>
          <p className="mt-5 text-base md:text-lg text-[#4A3F2F] max-w-2xl leading-relaxed">
            Cartographie complète de vos dynamiques internes : synergies réelles,
            frictions latentes, angles morts collectifs et scripts managériaux
            adaptés — déjà générés, prêts à être consultés.
          </p>

          {/* Distribution visible (révélation partielle) */}
          <div className="mt-8 bg-white border border-[#EDE4D0] rounded-2xl p-5 md:p-7" data-testid="pulse-teaser-distribution">
            <div className="text-[11px] tracking-[0.2em] uppercase text-[#8A7A5E] mb-4">
              Empreinte énergétique de votre équipe
            </div>
            <div className="space-y-2.5">
              {Object.entries(COLORS).map(([key, meta]) => {
                const val = distribution[key] || 0;
                const active = key === dominant;
                return (
                  <div key={key} className="flex items-center gap-3">
                    <div className={`w-20 md:w-24 text-[11px] uppercase tracking-wider ${active ? 'text-[#1F1A12] font-semibold' : 'text-[#8A7A5E]'}`}>
                      {meta.label}
                    </div>
                    <div className="flex-1 bg-[#FAF6EE] h-4 rounded overflow-hidden">
                      <div
                        className="h-full transition-all duration-700"
                        style={{ backgroundColor: meta.hex, width: `${Math.min(100, val)}%` }}
                      />
                    </div>
                    <div className="w-10 text-right text-xs tabular-nums font-medium" style={{ color: meta.hex }}>
                      {Math.round(val)}%
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* ──── Aperçu 2×2 — compteurs visibles, détails floutés ──── */}
        <div className="grid grid-cols-2 gap-3 md:gap-4 mb-10" data-testid="pulse-teaser-preview-grid">
          {[
            { k: 'synergies', label: 'Synergies détectées', n: counts.synergies, color: '#2D8659' },
            { k: 'frictions', label: 'Frictions latentes', n: counts.frictions, color: '#C0392B' },
            { k: 'blind_spots', label: 'Angles morts collectifs', n: counts.blind_spots, color: '#92710A' },
            { k: 'scripts', label: 'Scripts managériaux', n: counts.scripts, color: '#1E4F8C' },
          ].map((card) => (
            <div key={card.k} className="relative bg-white border border-[#EDE4D0] rounded-2xl p-5 md:p-6 overflow-hidden">
              <div className="text-[11px] uppercase tracking-wider text-[#8A7A5E]">
                {card.label}
              </div>
              <div className="mt-2 font-serif text-4xl md:text-5xl tabular-nums" style={{ color: card.color }}>
                {card.n}
              </div>
              <div className="mt-3 space-y-1.5 filter blur-[3px] select-none pointer-events-none">
                <div className="h-2 bg-[#EDE4D0] rounded" style={{ width: '90%' }} />
                <div className="h-2 bg-[#EDE4D0] rounded" style={{ width: '75%' }} />
                <div className="h-2 bg-[#EDE4D0] rounded" style={{ width: '82%' }} />
              </div>
              <div className="absolute top-3 right-3">
                <Lock className="h-3.5 w-3.5 text-[#8A7A5E]" />
              </div>
            </div>
          ))}
        </div>

        {/* ──── Preuve sociale ──── */}
        <div className="mb-8 text-center" data-testid="pulse-teaser-social-proof">
          <div className="inline-flex items-center gap-3 px-5 py-2.5 bg-white border border-[#EDE4D0] rounded-full">
            <div className="flex -space-x-2">
              {['#C0392B', '#D4A017', '#2D8659', '#1E4F8C'].map((c, i) => (
                <div key={i} className="w-6 h-6 rounded-full border-2 border-white" style={{ backgroundColor: c }} />
              ))}
            </div>
            <span className="text-xs text-[#4A3F2F]">
              +200 équipes ont déjà analysé leur dynamique avec COACHDIA
            </span>
          </div>
        </div>

        {/* ──── Desktop CTA card (inline) ──── */}
        <div className="hidden md:block bg-white border border-[#EDE4D0] rounded-2xl p-8 md:p-10 shadow-sm">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
            <div className="flex-1 min-w-0">
              <div className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-[#1F1A12] text-[#D4AF37] mb-3">
                <Lock className="h-4 w-4" />
              </div>
              <h2 className="text-2xl font-serif text-[#1F1A12] mb-2">
                Débloquez le rapport complet
              </h2>
              <ul className="text-sm text-[#4A3F2F] space-y-1.5 mt-3">
                <li className="flex items-start gap-2"><Check className="h-4 w-4 text-[#92710A] shrink-0 mt-0.5" /> Cartographie complète des 4 énergies</li>
                <li className="flex items-start gap-2"><Check className="h-4 w-4 text-[#92710A] shrink-0 mt-0.5" /> Synergies et frictions (qui avec qui, et pourquoi)</li>
                <li className="flex items-start gap-2"><Check className="h-4 w-4 text-[#92710A] shrink-0 mt-0.5" /> Scripts managériaux prêts à l'emploi</li>
                <li className="flex items-start gap-2"><Check className="h-4 w-4 text-[#92710A] shrink-0 mt-0.5" /> Téléchargement PDF premium</li>
              </ul>
            </div>

            <div className="w-full md:w-auto md:min-w-[260px] text-center md:text-right">
              {priceEur != null && (
                <div className="mb-4" data-testid="pulse-teaser-price-block">
                  <div className="flex items-baseline justify-end gap-2">
                    {promoActive && basePrice && (
                      <span className="text-sm line-through text-[#8A7A5E] tabular-nums">
                        {Number(basePrice).toFixed(0)} €
                      </span>
                    )}
                    <span className="text-4xl md:text-5xl font-serif text-[#1F1A12] tabular-nums" data-testid="pulse-teaser-price">
                      {Number(priceEur).toFixed(0)} €
                    </span>
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#8A7A5E] mt-1">
                    HT · paiement unique
                  </div>
                  {pack?.name && (
                    <div className="text-[11px] text-[#8A7A5E] mt-0.5">
                      Pack {pack.name} · {teamSize} profils
                    </div>
                  )}
                </div>
              )}
              <button
                onClick={onUnlock}
                disabled={unlocking}
                className="w-full inline-flex items-center justify-center gap-2 bg-[#1F1A12] text-white px-8 py-4 rounded-full font-medium text-sm hover:bg-[#2A2319] transition disabled:opacity-40 whitespace-nowrap"
                data-testid="pulse-unlock-report-btn"
              >
                {unlocking ? (
                  <><Loader2 className="h-4 w-4 animate-spin" /> Redirection sécurisée…</>
                ) : (
                  <>Débloquer le rapport <ArrowRight className="h-4 w-4" /></>
                )}
              </button>
              <p className="mt-3 text-[11px] text-[#8A7A5E]">
                Paiement sécurisé Stripe · accès immédiat
              </p>
              {batch?.stripe_session_id && !batch?.report_paid && onManualReconcile && (
                <button
                  type="button"
                  onClick={onManualReconcile}
                  disabled={unlocking}
                  className="mt-4 text-[11px] text-[#92710A] hover:text-[#1F1A12] underline underline-offset-2 disabled:opacity-40"
                  data-testid="pulse-reconcile-payment-btn"
                >
                  Vous avez déjà payé ? Vérifier mon paiement
                </button>
              )}
            </div>
          </div>

          {/* Rassurance */}
          <div className="mt-8 pt-6 border-t border-[#EDE4D0] flex flex-wrap items-center justify-center gap-6 text-[11px] text-[#8A7A5E]">
            <span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5" /> Paiement Stripe certifié</span>
            <span className="inline-flex items-center gap-1.5"><Lock className="h-3.5 w-3.5" /> Données RGPD</span>
            <span className="inline-flex items-center gap-1.5"><FileText className="h-3.5 w-3.5" /> PDF premium inclus</span>
          </div>
        </div>
      </div>

      {/* ──── Mobile CTA sticky bottom ──── */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-[#EDE4D0] p-4 shadow-2xl" data-testid="pulse-teaser-mobile-cta">
        <div className="flex items-center justify-between gap-3 mb-3">
          <div className="flex-1 min-w-0">
            {priceEur != null ? (
              <div>
                <div className="flex items-baseline gap-1.5">
                  {promoActive && basePrice && (
                    <span className="text-xs line-through text-[#8A7A5E] tabular-nums">
                      {Number(basePrice).toFixed(0)} €
                    </span>
                  )}
                  <span className="text-2xl font-serif text-[#1F1A12] tabular-nums">
                    {Number(priceEur).toFixed(0)} €
                  </span>
                  <span className="text-[10px] uppercase tracking-wider text-[#8A7A5E]">HT</span>
                </div>
                <div className="text-[10px] text-[#8A7A5E]">Paiement unique · PDF inclus</div>
              </div>
            ) : (
              <div className="text-xs text-[#8A7A5E]">Paiement sécurisé Stripe</div>
            )}
          </div>
        </div>
        <button
          onClick={onUnlock}
          disabled={unlocking}
          className="w-full inline-flex items-center justify-center gap-2 bg-[#1F1A12] text-white px-6 py-3.5 rounded-full font-medium text-sm disabled:opacity-40"
          data-testid="pulse-unlock-report-mobile-btn"
        >
          {unlocking ? (
            <><Loader2 className="h-4 w-4 animate-spin" /> Redirection…</>
          ) : (
            <>Débloquer le rapport <ArrowRight className="h-4 w-4" /></>
          )}
        </button>
        {batch?.stripe_session_id && !batch?.report_paid && onManualReconcile && (
          <button
            type="button"
            onClick={onManualReconcile}
            disabled={unlocking}
            className="mt-2 w-full text-[11px] text-[#92710A] underline underline-offset-2 disabled:opacity-40"
            data-testid="pulse-reconcile-payment-mobile-btn"
          >
            Vous avez déjà payé ? Vérifier mon paiement
          </button>
        )}
      </div>
    </div>
  );
};

// =============================================================================
// PHASE D — RAPPORT FINAL (Iter258 — 10 blocs structurés)
// =============================================================================
const SECTION_NAV = [
  { id: 'tldr',    label: 'TL;DR exécutif' },
  { id: 'stance',  label: 'Position stratégique' },
  { id: 'economic',label: 'Impact économique' },
  { id: 'exec',    label: 'Synthèse exécutive' },
  { id: 'dna',     label: 'ADN comportemental' },
  { id: 'dyn',     label: 'Dynamique interne' },
  { id: 'risks',   label: 'Risques CT/MT/LT' },
  { id: 'loops',   label: 'Boucles systémiques' },
  { id: 'triggers',label: 'Déclencheurs de décision' },
  { id: 'levers',  label: 'Leviers' },
  { id: 'matrix',  label: 'Matrice Impact/Effort' },
  { id: 'plan',    label: 'Plan 90 jours' },
  { id: 'members', label: 'Participants' },
  { id: 'scenar',  label: 'Scénarios' },
  { id: 'coach',   label: 'Coaching' },
];

/**
 * 🆕 Iter286 — Composant PARTAGÉ `<PulseReportView/>`
 * ===================================================
 * Vue unique du rapport stratégique. Utilisée :
 *  - par TeamPulse (mode rapport classique : back + download PDF)
 *  - par la Salle de Restitution (mode facilitation : focusedSection +
 *    onFocusChange pour scroll/highlight piloté par l'IA via tool focus_section)
 *
 * Props :
 *  - batch, report (obligatoires)
 *  - onBackToDashboard, onDownloadPdf, downloadingPdf (optionnels)
 *  - focusedSection (optionnel) : id de section à mettre en évidence
 *  - hideSidebarNav (optionnel) : masque la nav latérale (utile salle)
 *  - mode (optionnel) : 'all' (défaut) | 'single'
 *      • 'all'    : toutes les sections affichées + scroll vers focusedSection
 *      • 'single' : SEULE la section focusedSection est rendue (Iter289 salle)
 */
export const PulseReportView = ({
  batch, report,
  onBackToDashboard, onDownloadPdf, downloadingPdf,
  focusedSection, hideSidebarNav = false, mode = 'all',
}) => {
  const isSingle = mode === 'single';
  const size = report?.team_size || 0;
  const dominant = report?.team_dominant || 'red';
  const colorMeta = COLORS[dominant] || COLORS.red;
  const distribution = report?.distribution || {};
  const exec = report?.executive_summary || {};
  const dna = report?.dna || {};
  const rh = report?.risks_horizon || {};
  const la = report?.levers_by_axis || {};
  const ap = report?.action_plan_90d || {};
  const sc = report?.scenarios || {};
  const co = report?.coaching_recommendation || {};
  const members = report?.per_member_guide || [];
  const synergies = report?.synergies || [];
  const frictions = report?.frictions || [];
  const invisible = report?.invisible_tensions || [];
  const benchmark = report?.benchmark || {};
  // 🆕 V2 Iter260
  const stance = report?.strategic_stance || {};
  const tldr = report?.executive_tldr || {};
  const triggers = report?.decision_triggers || [];
  const matrix = report?.impact_effort_matrix || {};
  // 🆕 V3 Iter262 P0 — Moteur économique + boucles systémiques + contexte
  const economic = report?.economic_engine || {};
  const systemicLoops = report?.systemic_loops || [];
  const sectorCtx = report?.sector_context || {};
  const eurFmt = (n) => (typeof n === 'number' ? new Intl.NumberFormat('fr-FR').format(Math.round(n)) : '—');

  const scroll = (id) => {
    const el = document.getElementById(`pulse-sec-${id}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // 🆕 Iter287/289 — Auto-scroll + highlight pilotés par l'IA + mode 'single'.
  React.useEffect(() => {
    if (focusedSection && !isSingle) scroll(focusedSection);
  }, [focusedSection, isSingle]);
  const sectionFocusClass = (id) => {
    // Mode single : sections non-focus sont cachées (rendu nul)
    if (isSingle && id !== focusedSection) return 'hidden';
    const base = 'transition-all duration-500 rounded-2xl scroll-mt-24';
    if (isSingle) {
      // En mode single, halo doré permanent sur la seule section affichée
      return `${base} ring-2 ring-[#F3D060] ring-offset-4 ring-offset-[#F5F1EA] -mx-3 px-4 py-4 bg-[#FBF8F1]/40`;
    }
    return focusedSection === id
      ? `${base} ring-2 ring-[#F3D060] ring-offset-4 ring-offset-[#F5F1EA] shadow-[0_0_60px_-15px_rgba(243,208,96,0.55)] -mx-3 px-3 py-3 bg-[#FBF8F1]/40`
      : `${base} -mx-3 px-3`;
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-[#F5F1EA]" data-testid="pulse-report-screen">
      <div className="max-w-6xl mx-auto px-6 py-8 md:py-12">
        {/* ── Toolbar (cachée en mode salle de restitution) ── */}
        {(onBackToDashboard || onDownloadPdf) && (
          <div className="flex items-center justify-between mb-10 gap-4 flex-wrap">
            {onBackToDashboard ? (
              <button
                onClick={onBackToDashboard}
                className="text-xs text-[#92710A] hover:text-[#1F1A12] inline-flex items-center gap-1.5"
                data-testid="pulse-report-back-btn"
              >
                <ChevronLeft className="h-3.5 w-3.5" /> Retour à la progression
              </button>
            ) : <span />}
            <div className="flex items-center gap-4">
              {onDownloadPdf && (
                <button
                  onClick={onDownloadPdf}
                  disabled={downloadingPdf}
                  className="inline-flex items-center gap-2 bg-[#1F1A12] text-white px-5 py-2.5 rounded-full text-xs font-medium hover:bg-[#2A2319] transition disabled:opacity-40"
                  data-testid="pulse-report-pdf-btn"
                >
                  {downloadingPdf ? (
                    <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Préparation…</>
                  ) : (
                    <><Download className="h-3.5 w-3.5" /> Télécharger le PDF</>
                  )}
                </button>
              )}
              <div className="text-[11px] tracking-[0.28em] uppercase text-[#92710A] font-medium hidden md:block">
                COACHDIA Pulse · Rapport
              </div>
            </div>
          </div>
        )}

        {/* ── Hero ── */}
        <div className="h-px w-16 bg-gradient-to-r from-[#92710A] to-[#D4AF37] mb-5" />
        <div className="text-[11px] tracking-[0.28em] uppercase text-[#92710A] font-medium">
          Rapport stratégique de dynamique comportementale
        </div>
        <h1 className="mt-2 text-4xl md:text-6xl font-serif leading-[1.02] text-[#1F1A12]">
          {batch.name}
          <span className="block italic text-[#92710A] mt-2 text-2xl md:text-3xl">
            {size} profils · dominante {colorMeta.label.toLowerCase()}
          </span>
        </h1>
        {benchmark.headline && (
          <div className="mt-5 inline-flex items-center gap-2 px-4 py-2 bg-[#FBF8F1] border border-[#EDE4D0] rounded-full" data-testid="pulse-report-benchmark">
            <Sparkles className="h-3.5 w-3.5 text-[#92710A]" />
            <span className="text-xs md:text-sm text-[#4A3F2F] italic">{benchmark.headline}</span>
          </div>
        )}

        <div className="mt-10 grid grid-cols-12 gap-8">
          {/* ── Sidebar navigation (cachée en mode salle si hideSidebarNav) ── */}
          {!hideSidebarNav && (
            <aside className="hidden lg:block col-span-3 sticky top-24 self-start" data-testid="pulse-report-nav">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#8A7A5E] mb-4">Sections</div>
              <nav className="space-y-1">
                {SECTION_NAV.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => scroll(s.id)}
                    className={`block w-full text-left text-xs px-3 py-2 rounded transition ${
                      focusedSection === s.id
                        ? 'bg-[#F3D060]/15 text-[#92710A] font-semibold'
                        : 'text-[#4A3F2F] hover:text-[#1F1A12] hover:bg-[#FBF8F1]'
                    }`}
                    data-testid={`pulse-report-nav-${s.id}`}
                  >
                    {s.label}
                  </button>
                ))}
              </nav>
            </aside>
          )}

          {/* ── Main content ── */}
          <div className={`col-span-12 ${hideSidebarNav ? '' : 'lg:col-span-9'} space-y-10`}>

            {/* 🆕 V2 — Executive TL;DR (lecture 30 sec) */}
            {tldr.title && (
              <section id="pulse-sec-tldr" data-testid="pulse-sec-tldr" className={sectionFocusClass('tldr')}>
                <SectionHeader n="01" title={tldr.title} sub="Votre rapport en 30 secondes · prêt pour la direction." />
                <div className="bg-gradient-to-br from-[#1F1A12] to-[#2A2319] text-white rounded-2xl p-6 md:p-8 shadow-xl">
                  {tldr.one_line_verdict && (
                    <div className="text-lg md:text-xl leading-snug font-serif italic mb-5" data-testid="pulse-tldr-verdict">
                      « {tldr.one_line_verdict} »
                    </div>
                  )}
                  {tldr.top_number?.value && (
                    <div className="inline-flex flex-col items-start mb-6 px-5 py-3 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-xl">
                      <div className="text-3xl md:text-4xl font-serif text-[#D4AF37]">{tldr.top_number.value}</div>
                      <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37]/80 mt-1">{tldr.top_number.label}</div>
                    </div>
                  )}
                  {Array.isArray(tldr.three_takeaways) && tldr.three_takeaways.length > 0 && (
                    <div className="grid md:grid-cols-3 gap-3 mb-5" data-testid="pulse-tldr-takeaways">
                      {tldr.three_takeaways.slice(0, 3).map((t, i) => (
                        <div key={i} className="bg-white/5 border border-white/10 rounded-lg p-4">
                          <div className="text-[10px] tracking-[0.24em] uppercase text-[#D4AF37] mb-2">{['Force', 'Risque', 'Premier pas'][i]}</div>
                          <div className="text-sm leading-relaxed">{t}</div>
                        </div>
                      ))}
                    </div>
                  )}
                  {tldr.next_step && (
                    <div className="text-xs md:text-sm text-white/80 border-l-2 border-[#D4AF37] pl-4 italic">
                      <span className="not-italic uppercase tracking-[0.2em] text-[10px] text-[#D4AF37]">Next step · </span>
                      {tldr.next_step}
                    </div>
                  )}
                </div>
              </section>
            )}

            {/* 🆕 V2 — Strategic Stance (verdict tranché) */}
            {stance.verdict && (
              <section id="pulse-sec-stance" data-testid="pulse-sec-stance" className={sectionFocusClass('stance')}>
                <SectionHeader n="02" title="Position stratégique" sub="Verdict tranché · maturité · pari recommandé." />
                <div className="bg-white border border-[#EDE4D0] rounded-2xl p-6 md:p-8 shadow-sm">
                  <div className="text-[10px] uppercase tracking-[0.28em] text-[#92710A] mb-2">Verdict</div>
                  <div className="font-serif text-2xl md:text-3xl text-[#1F1A12] leading-tight mb-6" data-testid="pulse-stance-verdict">
                    {stance.verdict}
                  </div>
                  <div className="grid md:grid-cols-3 gap-6 mb-6">
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.24em] text-[#8A7A5E] mb-2">Maturité</div>
                      <div className="flex items-center gap-2" data-testid="pulse-stance-maturity">
                        {[1, 2, 3, 4, 5].map((i) => (
                          <div key={i} className={`h-2 flex-1 rounded-full ${i <= (stance.maturity_level || 0) ? 'bg-[#92710A]' : 'bg-[#EDE4D0]'}`} />
                        ))}
                      </div>
                      <div className="text-xs text-[#4A3F2F] italic mt-2">{stance.maturity_label}</div>
                    </div>
                    {stance.confidence != null && (
                      <div>
                        <div className="text-[10px] uppercase tracking-[0.24em] text-[#8A7A5E] mb-2">Confiance</div>
                        <div className="text-2xl font-serif text-[#1F1A12]">{Math.round((stance.confidence || 0) * 100)}%</div>
                        <div className="text-xs text-[#4A3F2F]">fondée sur les signaux comportementaux</div>
                      </div>
                    )}
                  </div>
                  {stance.central_stake && (
                    <div className="bg-[#FBF8F1] border-l-4 border-[#C0392B] p-4 rounded-r-lg mb-4" data-testid="pulse-stance-stake">
                      <div className="text-[10px] uppercase tracking-[0.24em] text-[#C0392B] mb-1">Enjeu central</div>
                      <div className="text-sm text-[#1F1A12] leading-relaxed">{stance.central_stake}</div>
                    </div>
                  )}
                  {stance.strategic_bet && (
                    <div className="bg-[#FBF8F1] border-l-4 border-[#2D8659] p-4 rounded-r-lg" data-testid="pulse-stance-bet">
                      <div className="text-[10px] uppercase tracking-[0.24em] text-[#2D8659] mb-1">Pari stratégique</div>
                      <div className="text-sm text-[#1F1A12] leading-relaxed">{stance.strategic_bet}</div>
                    </div>
                  )}
                </div>
              </section>
            )}

            {/* 🆕 V3 P0 — Impact économique (source de vérité chiffrée) */}
            {economic.mode && (
              <section id="pulse-sec-economic" data-testid="pulse-sec-economic" className={sectionFocusClass('economic')}>
                <SectionHeader
                  n="03"
                  title="Impact économique"
                  sub={economic.mode === 'full_economic'
                    ? 'Coût de l\'inaction · ROI plan 90j · flight risk · calculs serveur-side.'
                    : 'Lecture comportementale — contextualisez pour débloquer les chiffres € réels.'}
                />

                {/* Contexte sectoriel pill */}
                {sectorCtx.sector_label && (
                  <div className="mb-4 flex flex-wrap gap-2 items-center text-xs" data-testid="pulse-sector-pills">
                    <span className="px-3 py-1 rounded-full bg-[#FBF8F1] border border-[#EDE4D0] text-[#4A3F2F]">
                      <span className="text-[#92710A] font-semibold">Secteur · </span>{sectorCtx.sector_label}
                    </span>
                    <span className="px-3 py-1 rounded-full bg-[#FBF8F1] border border-[#EDE4D0] text-[#4A3F2F]">
                      <span className="text-[#92710A] font-semibold">Stade · </span>{sectorCtx.stage_label}
                    </span>
                    <span className="px-3 py-1 rounded-full bg-[#FBF8F1] border border-[#EDE4D0] text-[#4A3F2F]">
                      <span className="text-[#92710A] font-semibold">Pression · </span>{sectorCtx.competitive_pressure}
                    </span>
                    <span className="px-3 py-1 rounded-full bg-[#1F1A12] text-[#D4AF37]">
                      Tax de vélocité : {economic.velocity_tax?.contextualised_tax_pct ?? '—'} %
                    </span>
                  </div>
                )}

                {economic.mode === 'full_economic' ? (
                  <>
                    {/* 3 KPI hero */}
                    <div className="grid md:grid-cols-3 gap-3 mb-5">
                      <div className="bg-white border border-[#EDE4D0] rounded-2xl p-5" data-testid="pulse-eco-cost-inaction">
                        <div className="text-[10px] uppercase tracking-[0.24em] text-[#C0392B] mb-2">Coût de l'inaction</div>
                        <div className="font-serif text-3xl text-[#1F1A12] leading-none">
                          {eurFmt(economic.cost_of_inaction?.annual_eur)} €
                        </div>
                        <div className="text-xs text-[#4A3F2F] mt-2">par an · intervalle {eurFmt(economic.cost_of_inaction?.annual_eur_range?.[0])}–{eurFmt(economic.cost_of_inaction?.annual_eur_range?.[1])} €</div>
                        {economic.cost_of_inaction?.breakdown_label && (
                          <div className="mt-3 text-[11px] text-[#8A7A5E] italic leading-relaxed border-t border-[#EDE4D0] pt-2">
                            {economic.cost_of_inaction.breakdown_label}
                          </div>
                        )}
                      </div>
                      <div className="bg-white border border-[#EDE4D0] rounded-2xl p-5" data-testid="pulse-eco-roi">
                        <div className="text-[10px] uppercase tracking-[0.24em] text-[#2D8659] mb-2">ROI plan 90 jours</div>
                        <div className="font-serif text-3xl text-[#1F1A12] leading-none">
                          +{eurFmt(economic.roi_projection_12m?.net_roi_12m_eur)} €
                        </div>
                        <div className="text-xs text-[#4A3F2F] mt-2">net à 12 mois · multiple <b>{economic.roi_projection_12m?.roi_multiple}×</b></div>
                        {economic.roi_projection_12m?.payback_months != null && (
                          <div className="mt-3 text-[11px] text-[#8A7A5E] italic leading-relaxed border-t border-[#EDE4D0] pt-2">
                            Retour sur investissement en {economic.roi_projection_12m.payback_months} mois — coût plan : {eurFmt(economic.roi_projection_12m.plan_cost_eur)} €
                          </div>
                        )}
                      </div>
                      <div className="bg-white border border-[#EDE4D0] rounded-2xl p-5" data-testid="pulse-eco-flight">
                        <div className="text-[10px] uppercase tracking-[0.24em] text-[#92710A] mb-2">Flight risk 12 mois</div>
                        <div className="font-serif text-3xl text-[#1F1A12] leading-none">
                          {Math.round((economic.turnover_risk?.probability_12m || 0) * 100)} %
                        </div>
                        <div className="text-xs text-[#4A3F2F] mt-2">{economic.turnover_risk?.probability_label}</div>
                        <div className="mt-3 text-[11px] text-[#8A7A5E] italic leading-relaxed border-t border-[#EDE4D0] pt-2">
                          Coût attendu : {eurFmt(economic.turnover_risk?.expected_turnover_cost_annual_eur)} €/an
                        </div>
                      </div>
                    </div>

                    {/* Hypothèses publiées (défense analytique) */}
                    {Array.isArray(economic.assumptions) && (
                      <div className="bg-[#1F1A12] text-white/90 rounded-xl p-4" data-testid="pulse-eco-assumptions">
                        <div className="text-[10px] uppercase tracking-[0.24em] text-[#D4AF37] mb-2">Hypothèses · intervalle ±25 %</div>
                        <ul className="text-xs space-y-1 leading-relaxed">
                          {economic.assumptions.map((a, i) => (
                            <li key={i} className="flex gap-2"><span className="text-[#D4AF37]">·</span><span>{a}</span></li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="bg-[#FBF8F1] border border-[#EDE4D0] rounded-2xl p-5" data-testid="pulse-eco-behavioral-only">
                    <div className="text-sm text-[#1F1A12] leading-relaxed">
                      <b>Mode comportemental :</b> tax de vélocité observée de <b>{economic.velocity_tax?.contextualised_tax_pct} %</b>{' '}
                      — renseignez secteur, stade, bandes de payroll et deal size pour débloquer le chiffrage € détaillé
                      (coût inaction annualisé, ROI plan 90j, payback).
                    </div>
                  </div>
                )}
              </section>
            )}

            {/* 2 — Synthèse exécutive */}
            <section id="pulse-sec-exec" data-testid="pulse-sec-exec" className={sectionFocusClass('exec')}>
              <SectionHeader n="04" title="Synthèse exécutive" sub="Diagnostic stratégique — 3 forces · 3 risques · 3 actions." />
              {exec.diagnostic && (
                <div className="bg-[#1F1A12] text-white rounded-2xl p-6 md:p-8 mb-6">
                  <div className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] mb-3">Diagnostic</div>
                  <p className="text-base md:text-lg leading-relaxed">{exec.diagnostic}</p>
                </div>
              )}
              <div className="grid md:grid-cols-3 gap-4">
                <TryCard title="3 forces majeures" color="#2D8659" items={exec.top_strengths} metaKey="evidence" />
                <TryCard title="3 risques critiques" color="#C0392B" items={exec.critical_risks} metaKey="impact" metaLabel="Impact :" />
                <TryCard title="3 actions prioritaires" color="#92710A" items={exec.priority_actions} metaKey="deadline" metaLabel="Échéance :" />
              </div>
            </section>

            {/* 3 — ADN */}
            <section id="pulse-sec-dna" data-testid="pulse-sec-dna" className={sectionFocusClass('dna')}>
              <SectionHeader n="05" title="ADN comportemental" sub="Signature collective + lecture stratégique." />
              <div className="bg-white border border-[#EDE4D0] rounded-2xl p-6 md:p-8">
                {dna.oneliner && <p className="text-xl md:text-2xl font-serif text-[#1F1A12] italic mb-4">« {dna.oneliner} »</p>}
                {dna.identity && <KV label="Identité" value={dna.identity} />}
                {dna.dominant_dynamic && <KV label="Dynamique dominante" value={dna.dominant_dynamic} />}
                {dna.hidden_risk && <KV label="Risque invisible" value={dna.hidden_risk} />}
                {dna.strategic_interpretation && <KV label="Lecture stratégique" value={dna.strategic_interpretation} highlight />}
              </div>
              <div className="mt-6 bg-white border border-[#EDE4D0] rounded-2xl p-6">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#8A7A5E] mb-4">Empreinte énergétique</div>
                <div className="space-y-2.5">
                  {Object.entries(COLORS).map(([key, meta]) => {
                    const val = distribution[key] || 0;
                    return (
                      <div key={key} className="flex items-center gap-4">
                        <div className="w-28 text-xs uppercase tracking-wider text-[#8A7A5E]">{meta.label}</div>
                        <div className="flex-1 bg-[#FAF6EE] h-5 rounded overflow-hidden">
                          <div className="h-full transition-all" style={{ backgroundColor: meta.hex, width: `${Math.min(100, val)}%` }} />
                        </div>
                        <div className="w-12 text-right text-sm font-semibold tabular-nums" style={{ color: meta.hex }}>
                          {Math.round(val)}%
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </section>

            {/* 4 — Dynamique interne */}
            <section id="pulse-sec-dyn" data-testid="pulse-sec-dyn" className={sectionFocusClass('dyn')}>
              <SectionHeader n="06" title="Dynamique interne de l'équipe" sub="Synergies · frictions · tensions invisibles." />
              <div className="grid md:grid-cols-2 gap-4 mb-6">
                <div className="bg-white border border-[#EDE4D0] rounded-2xl p-6">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-700 mb-3">Synergies détectées</div>
                  {synergies.length === 0 && <p className="text-sm text-[#8A7A5E]">Aucune synergie majeure détectée.</p>}
                  {synergies.slice(0, 4).map((s, i) => (
                    <div key={i} className="mb-4 pb-4 border-b border-[#EDE4D0] last:border-0">
                      <div className="font-semibold text-sm text-[#1F1A12] mb-1">{(s.members || []).join(' × ')}</div>
                      <p className="text-xs text-[#4A3F2F] leading-relaxed">{s.strength}</p>
                      <p className="text-xs text-[#92710A] mt-1">→ {s.amplification}</p>
                    </div>
                  ))}
                </div>
                <div className="bg-white border border-[#EDE4D0] rounded-2xl p-6">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-red-700 mb-3">Frictions à désamorcer</div>
                  {frictions.length === 0 && <p className="text-sm text-[#8A7A5E]">Aucune friction majeure.</p>}
                  {frictions.slice(0, 4).map((f, i) => (
                    <div key={i} className="mb-4 pb-4 border-b border-[#EDE4D0] last:border-0">
                      <div className="font-semibold text-sm text-[#1F1A12] mb-1">{(f.members || []).join(' × ')}</div>
                      <p className="text-xs text-[#4A3F2F]"><b>Symptôme :</b> {f.visible_symptom}</p>
                      <p className="text-xs text-[#4A3F2F] mt-1"><b>Racine :</b> {f.root_cause}</p>
                      <p className="text-xs text-[#92710A] mt-1">→ {f.unblock}</p>
                    </div>
                  ))}
                </div>
              </div>
              {invisible.length > 0 && (
                <div className="bg-[#FBF8F1] border-l-4 border-[#92710A] rounded-r-xl p-5">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#92710A] mb-2">Tensions invisibles</div>
                  <ul className="space-y-1.5 text-sm text-[#4A3F2F]">
                    {invisible.slice(0, 4).map((t, i) => (<li key={i}>— {t}</li>))}
                  </ul>
                </div>
              )}
            </section>

            {/* 5 — Risques CT/MT/LT */}
            <section id="pulse-sec-risks" data-testid="pulse-sec-risks" className={sectionFocusClass('risks')}>
              <SectionHeader n="07" title="Risques opérationnels par horizon" sub="30 jours · 6 mois · 12 mois. Typologie + mitigation." />
              <div className="grid md:grid-cols-3 gap-4">
                <HorizonCard label="Court terme · 30j" items={rh.short_term_30d} color="#C0392B" />
                <HorizonCard label="Moyen terme · 3-6m" items={rh.mid_term_6m} color="#92710A" />
                <HorizonCard label="Long terme · 6-12m" items={rh.long_term_12m} color="#1E4F8C" />
              </div>
            </section>

            {/* 🆕 V3 P0 — Boucles systémiques (prédiction vs description) */}
            {Array.isArray(systemicLoops) && systemicLoops.length > 0 && (
              <section id="pulse-sec-loops" data-testid="pulse-sec-loops" className={sectionFocusClass('loops')}>
                <SectionHeader n="08" title="Boucles systémiques" sub="Mécanismes d'amplification · horizon de rupture · levier de désamorçage." />
                <div className="space-y-3">
                  {systemicLoops.map((lp, i) => {
                    const isReinforcing = lp.feedback_type === 'reinforcing';
                    const color = isReinforcing ? '#C0392B' : '#2D8659';
                    const tag = isReinforcing ? 'Boucle d\'amplification' : 'Boucle stabilisatrice';
                    return (
                      <div
                        key={i}
                        className="bg-white border border-[#EDE4D0] rounded-2xl p-5"
                        style={{ borderLeftColor: color, borderLeftWidth: '4px' }}
                        data-testid={`pulse-loop-${i}`}
                      >
                        <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
                          <div>
                            <div className="text-[10px] uppercase tracking-[0.24em] font-semibold mb-1" style={{ color }}>{tag}</div>
                            <div className="text-lg font-serif text-[#1F1A12]">{lp.label}</div>
                          </div>
                          <span className="text-[10px] uppercase tracking-[0.2em] text-[#8A7A5E] px-3 py-1 rounded-full bg-[#FBF8F1] whitespace-nowrap">
                            Horizon · {lp.time_horizon}
                          </span>
                        </div>
                        <div className="text-xs text-[#4A3F2F] mb-2"><b className="text-[#92710A]">Déclencheur · </b>{lp.trigger}</div>
                        <div className="text-sm text-[#1F1A12] leading-relaxed mb-3">{lp.amplifier}</div>
                        {lp.breaking_point && (
                          <div className="text-xs text-[#4A3F2F] italic leading-relaxed mb-2 border-t border-[#EDE4D0] pt-2">
                            <b className="text-[#C0392B] not-italic">Point de rupture · </b>{lp.breaking_point}
                          </div>
                        )}
                        {lp.breaker_lever && (
                          <div className="text-xs text-[#4A3F2F] leading-relaxed">
                            <b className="text-[#2D8659]">Levier de désamorçage · </b>{lp.breaker_lever}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            {/* 🆕 V2 — Decision Triggers (signaux observables) */}
            {Array.isArray(triggers) && triggers.length > 0 && (
              <section id="pulse-sec-triggers" data-testid="pulse-sec-triggers" className={sectionFocusClass('triggers')}>
                <SectionHeader n="09" title="Déclencheurs de décision" sub="Signaux observables + seuils + leviers à actionner." />

                {/* 📱 Mobile — cards stack (lisible, zero overflow) */}
                <div className="space-y-3 md:hidden">
                  {triggers.map((t, i) => (
                    <div
                      key={i}
                      className="bg-white border border-[#EDE4D0] rounded-2xl p-5 shadow-sm"
                      data-testid={`pulse-trigger-card-${i}`}
                    >
                      <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-[#8A7A5E]">{t.typology}</div>
                        {t.threshold && (
                          <span className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#C0392B] whitespace-nowrap">
                            {t.threshold}
                          </span>
                        )}
                      </div>
                      <div className="text-sm font-semibold text-[#1F1A12] mb-3 leading-snug break-words">{t.signal}</div>
                      <div className="border-t border-[#EDE4D0] pt-3">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-[#92710A] mb-1">Levier à actionner</div>
                        <div className="text-sm text-[#1F1A12] leading-relaxed break-words">{t.action_lever}</div>
                        {t.escalation && (
                          <div className="mt-2 text-xs text-[#4A3F2F] italic leading-relaxed">↳ {t.escalation}</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* 🖥️ Desktop — table premium avec scroll horizontal si overflow */}
                <div className="hidden md:block overflow-x-auto bg-white border border-[#EDE4D0] rounded-2xl">
                  <table className="w-full text-sm" style={{ tableLayout: 'fixed' }}>
                    <colgroup>
                      <col style={{ width: '45%' }} />
                      <col style={{ width: '15%' }} />
                      <col style={{ width: '40%' }} />
                    </colgroup>
                    <thead className="bg-[#FBF8F1]">
                      <tr>
                        <th className="px-4 py-3 text-left text-[10px] uppercase tracking-[0.22em] text-[#92710A] font-medium">Signal observable</th>
                        <th className="px-4 py-3 text-left text-[10px] uppercase tracking-[0.22em] text-[#92710A] font-medium">Seuil</th>
                        <th className="px-4 py-3 text-left text-[10px] uppercase tracking-[0.22em] text-[#92710A] font-medium">Levier à actionner</th>
                      </tr>
                    </thead>
                    <tbody>
                      {triggers.map((t, i) => (
                        <tr key={i} className="border-t border-[#EDE4D0]" data-testid={`pulse-trigger-${i}`}>
                          <td className="px-4 py-4 text-[#1F1A12] font-medium align-top break-words">
                            <div className="break-words">{t.signal}</div>
                            {t.typology && (
                              <div className="mt-1 text-[10px] uppercase tracking-[0.18em] text-[#8A7A5E]">{t.typology}</div>
                            )}
                          </td>
                          <td className="px-4 py-4 text-[#C0392B] font-medium align-top break-words">{t.threshold}</td>
                          <td className="px-4 py-4 align-top break-words">
                            <div className="text-[#1F1A12] break-words">{t.action_lever}</div>
                            {t.escalation && (
                              <div className="mt-2 text-xs text-[#4A3F2F] italic break-words">↳ {t.escalation}</div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )}

            {/* 7 — Leviers 3 axes */}
            <section id="pulse-sec-levers" data-testid="pulse-sec-levers" className={sectionFocusClass('levers')}>
              <SectionHeader n="10" title="Leviers de performance" sub="Collectif · managérial · individuel. Priorisés P1-P3." />
              <div className="space-y-6">
                <AxisBlock title="Leviers collectifs" items={la.collective} />
                <AxisBlock title="Leviers managériaux" items={la.managerial} />
                <AxisBlock title="Leviers individuels" items={la.individual} />
              </div>
            </section>

            {/* 🆕 V2 — Impact / Effort Matrix (serveur-side computed) */}
            {matrix.quick_wins && (
              <section id="pulse-sec-matrix" data-testid="pulse-sec-matrix" className={sectionFocusClass('matrix')}>
                <SectionHeader n="11" title="Matrice Impact / Effort" sub="Priorisation visuelle · par où commencer." />
                <div className="grid grid-cols-2 gap-3">
                  <QuadrantCard title="Quick Wins" subtitle="Impact fort · Effort faible" color="#2D8659" items={matrix.quick_wins} testId="pulse-matrix-quick-wins" />
                  <QuadrantCard title="Big Bets" subtitle="Impact fort · Effort élevé" color="#92710A" items={matrix.big_bets} testId="pulse-matrix-big-bets" />
                  <QuadrantCard title="Incremental" subtitle="Impact modéré · Effort faible" color="#5B8FB9" items={matrix.incremental} testId="pulse-matrix-incremental" />
                  <QuadrantCard title="Time Sinks" subtitle="Impact modéré · Effort élevé" color="#8A7A5E" items={matrix.time_sinks} testId="pulse-matrix-time-sinks" muted />
                </div>
                {Array.isArray(matrix.recommended_first_3) && matrix.recommended_first_3.length > 0 && (
                  <div className="mt-5 bg-[#1F1A12] text-white rounded-xl p-5" data-testid="pulse-matrix-reco">
                    <div className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] mb-2">3 à lancer en priorité</div>
                    <ol className="space-y-1 text-sm">
                      {matrix.recommended_first_3.map((t, i) => (
                        <li key={i} className="flex gap-3">
                          <span className="text-[#D4AF37] font-serif text-lg w-5">{i + 1}.</span>
                          <span>{t}</span>
                        </li>
                      ))}
                    </ol>
                  </div>
                )}
              </section>
            )}

            {/* 9 — Plan 90 jours */}
            <section id="pulse-sec-plan" data-testid="pulse-sec-plan" className={sectionFocusClass('plan')}>
              <SectionHeader n="12" title="Plan d'action 90 jours" sub="Stabiliser · aligner · optimiser." />
              <div className="space-y-4">
                <PhaseCard label="30 JOURS · STABILISER" phase={ap.d30_stabilize} />
                <PhaseCard label="60 JOURS · ALIGNER" phase={ap.d60_align} />
                <PhaseCard label="90 JOURS · OPTIMISER" phase={ap.d90_optimize} />
              </div>
            </section>

            {/* 8 — Participants (🆕 Iter263 premium card avec monogramme + étoiles + grid 2×2) */}
            <section id="pulse-sec-members" data-testid="pulse-sec-members" className={sectionFocusClass('members')}>
              <SectionHeader n="13" title="Analyse des participants" sub="Guide opérationnel pour vos 1:1. Respect RGPD." />
              <div className="grid md:grid-cols-2 gap-4">
                {members.map((m, i) => {
                  const col = COLORS[m.dominant_color]?.hex || '#555';
                  const colLabel = COLORS[m.dominant_color]?.label || '';
                  const name = m.name || 'Participant';
                  const monoParts = name.split(/\s+/).filter(Boolean);
                  const mono = monoParts.length >= 2
                    ? (monoParts[0][0] + monoParts[monoParts.length - 1][0]).toUpperCase()
                    : (monoParts[0] || '?').slice(0, 2).toUpperCase();
                  const score = Math.max(0, Math.min(5, m.contribution_score || 0));
                  return (
                    <div
                      key={i}
                      className="bg-white border border-[#EDE4D0] rounded-2xl p-6 relative overflow-hidden"
                      data-testid={`pulse-member-card-${i}`}
                    >
                      {/* Bandeau latéral couleur profil */}
                      <div className="absolute left-0 top-0 bottom-0 w-1" style={{ backgroundColor: col }} />

                      {/* Header : monogramme + identité */}
                      <div className="flex items-start gap-4 mb-4">
                        <div
                          className="shrink-0 w-14 h-14 rounded-full flex items-center justify-center font-serif text-lg shadow-sm"
                          style={{ backgroundColor: col + '22', color: col, border: `1.5px solid ${col}55` }}
                          data-testid={`pulse-member-monogram-${i}`}
                        >
                          {mono}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-serif text-xl text-[#1F1A12] leading-tight" data-testid={`pulse-member-name-${i}`}>
                            {name}
                          </div>
                          <div className="flex items-center gap-2 mt-1 flex-wrap">
                            <span className="text-[10px] uppercase tracking-[0.2em]" style={{ color: col }}>{colLabel}</span>
                            {m.mbti_code && (
                              <span className="text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 bg-[#FBF8F1] border border-[#EDE4D0] rounded-full text-[#8A7A5E]">
                                {m.mbti_code}
                              </span>
                            )}
                          </div>
                          {score > 0 && (
                            <div className="mt-1.5 flex items-center gap-0.5" data-testid={`pulse-member-stars-${i}`}>
                              {[1, 2, 3, 4, 5].map((n) => (
                                <span
                                  key={n}
                                  className={`text-sm ${n <= score ? 'text-[#D4AF37]' : 'text-[#EDE4D0]'}`}
                                  aria-hidden
                                >★</span>
                              ))}
                              <span className="ml-2 text-[10px] text-[#8A7A5E] uppercase tracking-[0.16em]">Contribution</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {m.unique_value && (
                        <div className="mb-4 pl-4 border-l-2 border-[#D4AF37]/60 italic text-sm text-[#1F1A12] leading-relaxed">
                          « {m.unique_value} »
                        </div>
                      )}

                      {/* Grid 2×2 */}
                      <div className="grid grid-cols-2 gap-x-4 gap-y-3 text-xs text-[#4A3F2F]">
                        {m.how_to_manage && (
                          <div>
                            <div className="text-[9px] uppercase tracking-[0.22em] text-[#92710A] mb-1">Mode d'emploi</div>
                            <div className="text-[#1F1A12] leading-relaxed">{m.how_to_manage}</div>
                          </div>
                        )}
                        {m.what_they_need && (
                          <div>
                            <div className="text-[9px] uppercase tracking-[0.22em] text-[#2D8659] mb-1">Pour briller</div>
                            <div className="text-[#1F1A12] leading-relaxed">{m.what_they_need}</div>
                          </div>
                        )}
                        {m.what_blocks_them && (
                          <div>
                            <div className="text-[9px] uppercase tracking-[0.22em] text-[#C0392B] mb-1">Ce qui bloque</div>
                            <div className="text-[#1F1A12] leading-relaxed">{m.what_blocks_them}</div>
                          </div>
                        )}
                        {m.best_role_in_team && (
                          <div>
                            <div className="text-[9px] uppercase tracking-[0.22em] text-[#5B8FB9] mb-1">Rôle optimal</div>
                            <div className="text-[#1F1A12] leading-relaxed">{m.best_role_in_team}</div>
                          </div>
                        )}
                      </div>

                      {m.development_edge && (
                        <div className="mt-4 pt-4 border-t border-[#EDE4D0]">
                          <div className="text-[9px] uppercase tracking-[0.22em] text-[#8A7A5E] mb-1">Prochaine frontière</div>
                          <div className="text-xs text-[#4A3F2F] italic leading-relaxed">{m.development_edge}</div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>

            {/* 9 — Scénarios */}
            <section id="pulse-sec-scenar" data-testid="pulse-sec-scenar" className={sectionFocusClass('scenar')}>
              <SectionHeader n="14" title="Scénarios d'évolution" sub="Deux trajectoires — à vous de choisir." />
              <div className="grid md:grid-cols-2 gap-4">
                <ScenarioCard data={sc.no_action} variant="no" alertKey="warning" />
                <ScenarioCard data={sc.with_actions} variant="yes" alertKey="opportunity" />
              </div>
            </section>

            {/* 10 — Coaching CTA */}
            <section id="pulse-sec-coach" data-testid="pulse-sec-coach" className={sectionFocusClass('coach')}>
              <SectionHeader n="15" title="Recommandation coaching" sub="Pertinence · bénéfices · premier pas." />
              <div className="bg-gradient-to-br from-[#FBF8F1] to-[#FAF0DC] border border-[#D4AF37] rounded-2xl p-6 md:p-8">
                <div className="flex items-start gap-8 flex-wrap">
                  <div className="text-center shrink-0">
                    <div className="font-serif text-6xl text-[#92710A]" data-testid="pulse-coaching-score">
                      {co.relevance_score ?? '—'}<span className="text-2xl text-[#8A7A5E]">/5</span>
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[#8A7A5E]">Pertinence</div>
                  </div>
                  <div className="flex-1 min-w-[240px]">
                    <div className="text-xs uppercase tracking-[0.2em] text-[#92710A] font-semibold mb-2">
                      {co.type === 'individual' ? 'Coaching individuel ciblé' :
                       co.type === 'hybrid' ? 'Accompagnement hybride' :
                       'Coaching collectif'}
                    </div>
                    <p className="text-sm text-[#4A3F2F] mb-3">{co.rationale}</p>
                    {co.focus_areas?.length > 0 && (
                      <p className="text-xs text-[#4A3F2F] mb-2"><b>Axes :</b> {co.focus_areas.join(' · ')}</p>
                    )}
                    {co.expected_benefits?.length > 0 && (
                      <ul className="text-xs text-[#4A3F2F] space-y-1 mb-3">
                        {co.expected_benefits.map((b, i) => (
                          <li key={i} className="flex items-start gap-1.5">
                            <Check className="h-3 w-3 text-[#92710A] shrink-0 mt-0.5" /> {b}
                          </li>
                        ))}
                      </ul>
                    )}
                    {co.suggested_duration && (
                      <p className="text-xs text-[#8A7A5E] mb-1"><b>Durée :</b> {co.suggested_duration}</p>
                    )}
                    {co.first_step && (
                      <p className="text-xs text-[#8A7A5E] mb-4"><b>Premier pas :</b> {co.first_step}</p>
                    )}
                    <a
                      href="/marketplace"
                      className="inline-flex items-center gap-2 bg-[#1F1A12] text-white px-6 py-3 rounded-full text-sm font-medium hover:bg-[#2A2319] transition"
                      data-testid="pulse-report-coaches-cta"
                    >
                      Découvrir nos coachs certifiés <ArrowRight className="h-4 w-4" />
                    </a>
                  </div>
                </div>
              </div>
            </section>

            <div className="text-center text-[11px] text-[#8A7A5E] pt-8 border-t border-[#EDE4D0]">
              Rapport généré le {new Date(batch.report_generated_at || Date.now()).toLocaleDateString('fr-FR')} ·
              <span className="ml-1">Accès réservé au propriétaire de la campagne</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Composants atomiques du rapport ───
const SectionHeader = ({ n, title, sub }) => (
  <div className="mb-5">
    <div className="text-[10px] uppercase tracking-[0.28em] text-[#92710A] font-semibold">
      {n} · {title}
    </div>
    <div className="text-sm text-[#8A7A5E] mt-1">{sub}</div>
    <div className="h-px w-12 bg-gradient-to-r from-[#92710A] to-transparent mt-2" />
  </div>
);

const KV = ({ label, value, highlight }) => (
  <div className={`mb-3 ${highlight ? 'pt-3 mt-3 border-t border-[#EDE4D0]' : ''}`}>
    <div className="text-[10px] uppercase tracking-[0.2em] text-[#8A7A5E] mb-1">{label}</div>
    <div className={`text-sm leading-relaxed ${highlight ? 'text-[#1F1A12] font-medium' : 'text-[#4A3F2F]'}`}>{value}</div>
  </div>
);

const TryCard = ({ title, color, items = [], metaKey, metaLabel = '' }) => (
  <div className="bg-white border border-[#EDE4D0] rounded-2xl p-5 border-t-4" style={{ borderTopColor: color }}>
    <div className="text-[10px] uppercase tracking-[0.2em] text-[#8A7A5E] font-semibold mb-3">{title}</div>
    <ol className="space-y-3">
      {(items || []).slice(0, 3).map((it, i) => (
        <li key={i} className="text-sm">
          <div className="font-semibold text-[#1F1A12] mb-0.5">{i + 1}. {it.title}</div>
          <div className="text-xs text-[#8A7A5E]">{metaLabel} {it[metaKey]}</div>
        </li>
      ))}
    </ol>
  </div>
);

const HorizonCard = ({ label, items = [], color }) => (
  <div className="bg-white border border-[#EDE4D0] rounded-2xl p-5 border-l-4" style={{ borderLeftColor: color }}>
    <div className="text-[10px] uppercase tracking-[0.2em] font-semibold mb-3" style={{ color }}>{label}</div>
    <div className="space-y-3">
      {(items || []).slice(0, 3).map((r, i) => (
        <div key={i} className="pb-3 border-b border-[#EDE4D0] last:border-0">
          <div className="text-[10px] uppercase tracking-wider text-[#8A7A5E]">{r.typology || 'stratégique'}</div>
          <div className="font-semibold text-sm text-[#1F1A12] my-1">{r.risk}</div>
          <div className="text-xs text-[#4A3F2F]"><b>→</b> {r.mitigation}</div>
        </div>
      ))}
    </div>
  </div>
);

const AxisBlock = ({ title, items = [] }) => {
  if (!items || items.length === 0) return null;
  return (
    <div className="bg-white border border-[#EDE4D0] rounded-2xl p-6">
      <div className="text-sm font-semibold text-[#92710A] mb-3 pb-2 border-b border-[#EDE4D0]">{title}</div>
      <div className="space-y-3">
        {items.map((lev, i) => {
          const prio = (lev.priority || 'P2').toUpperCase();
          const prioColor = prio === 'P1' ? '#C0392B' : prio === 'P2' ? '#92710A' : '#8A7A5E';
          return (
            <div key={i} className="flex items-start gap-3 pb-3 border-b border-[#EDE4D0] last:border-0">
              <span className="shrink-0 px-2 py-0.5 text-[10px] font-bold text-white rounded" style={{ backgroundColor: prioColor }}>
                {prio}
              </span>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm text-[#1F1A12]">{lev.title}</div>
                <div className="text-xs text-[#4A3F2F] mt-0.5">{lev.action}</div>
                {lev.success_metric && (
                  <div className="text-xs italic text-[#8A7A5E] mt-0.5">Succès : {lev.success_metric}</div>
                )}
              </div>
              <div className="shrink-0 text-[10px] text-[#8A7A5E] whitespace-nowrap tabular-nums">
                E:{lev.effort || '?'} · I:{lev.impact || '?'}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const QuadrantCard = ({ title, subtitle, color, items = [], testId, muted = false }) => {
  const list = Array.isArray(items) ? items : [];
  return (
    <div
      className={`bg-white border border-[#EDE4D0] rounded-xl p-4 min-h-[140px] ${muted ? 'opacity-70' : ''}`}
      data-testid={testId}
    >
      <div className="flex items-start justify-between gap-2 mb-3">
        <div>
          <div className="font-semibold text-sm" style={{ color }}>{title}</div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#8A7A5E] mt-0.5">{subtitle}</div>
        </div>
        <div className="text-xs font-serif text-[#8A7A5E] tabular-nums">{list.length}</div>
      </div>
      {list.length === 0 ? (
        <div className="text-xs text-[#8A7A5E] italic">—</div>
      ) : (
        <ul className="space-y-1.5">
          {list.slice(0, 4).map((it, i) => (
            <li key={i} className="text-xs text-[#4A3F2F] flex items-start gap-1.5">
              <span className="shrink-0 mt-1 w-1 h-1 rounded-full" style={{ backgroundColor: color }} />
              <span className="flex-1">
                <span className="font-medium text-[#1F1A12]">{it.title}</span>
                {it.priority && <span className="ml-1 text-[10px] text-[#8A7A5E]">· {it.priority}</span>}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const PhaseCard = ({ label, phase }) => {
  if (!phase || !phase.actions) return null;
  return (
    <div className="bg-white border border-[#EDE4D0] rounded-2xl p-5 md:p-6">
      <span className="inline-block bg-[#1F1A12] text-[#D4AF37] px-3 py-1 text-[10px] uppercase tracking-[0.2em] rounded mb-2">
        {label}
      </span>
      <h3 className="font-serif text-lg text-[#1F1A12] mb-4">{phase.theme}</h3>
      <div className="space-y-2">
        {(phase.actions || []).map((a, i) => (
          <div key={i} className="grid grid-cols-12 gap-3 text-xs py-2 border-b border-[#EDE4D0] last:border-0">
            <div className="col-span-2 text-[#92710A] font-semibold">{a.week}</div>
            <div className="col-span-5 text-[#1F1A12]">{a.action}</div>
            <div className="col-span-2 text-[#8A7A5E]">{a.owner}</div>
            <div className="col-span-3 text-[#4A3F2F] italic">{a.kpi}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

const ScenarioCard = ({ data, variant, alertKey }) => {
  if (!data) return null;
  const isNo = variant === 'no';
  return (
    <div className={`rounded-2xl p-5 md:p-6 border ${isNo ? 'bg-[#FDECEA] border-[#E6B5B0]' : 'bg-[#EFF8F0] border-[#A6D5B5]'}`}>
      <h3 className="font-serif text-lg text-[#1F1A12] mb-4">{data.title}</h3>
      <div className="space-y-3 text-sm">
        <TL label="3 mois" value={data.at_3m} />
        <TL label="6 mois" value={data.at_6m} />
        <TL label="12 mois" value={data.at_12m} />
      </div>
      {data[alertKey] && (
        <div className={`mt-4 px-3 py-2 text-xs text-white rounded ${isNo ? 'bg-[#C0392B]' : 'bg-[#2D8659]'}`}>
          {data[alertKey]}
        </div>
      )}
    </div>
  );
};

const TL = ({ label, value }) => (
  <div>
    <div className="text-[10px] uppercase tracking-wider text-[#8A7A5E]">{label}</div>
    <div className="text-sm text-[#1F1A12]">{value}</div>
  </div>
);

// =============================================================================
// MAIN COMPONENT — state machine orchestrator
// =============================================================================
export default function TeamPulse() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [phase, setPhase] = useState('loading'); // loading | invite | dashboard | teaser | report
  const [batch, setBatch] = useState(null);
  const [invitations, setInvitations] = useState([]);
  const [progress, setProgress] = useState(null);
  const [report, setReport] = useState(null);
  const [pack, setPack] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [unlocking, setUnlocking] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [forceInvite, setForceInvite] = useState(false);

  // Auth gate
  useEffect(() => {
    if (!authLoading && !user) navigate('/auth');
  }, [authLoading, user, navigate]);

  const refreshBatch = useCallback(async (bid) => {
    try {
      const { data } = await axios.get(
        `${API}/team-pulse/batches/${bid}`, { headers: authHeaders() },
      );
      setBatch(data.batch);
      setInvitations(data.invitations || []);
      setProgress(data.progress);
      // 🎯 Iter257 — hydrate pack depuis batch si disponible (retour après génération)
      if (data.batch?.pack_snapshot) setPack(data.batch.pack_snapshot);
      if (data.batch.report_unlocked && data.batch.report_data) {
        setReport(data.batch.report_data);
        // Phase 4 : si payé → report direct, sinon teaser
        setPhase(data.batch.report_paid ? 'report' : 'teaser');
      } else {
        setPhase('dashboard');
      }
      return data;
    } catch (err) {
      // Silencieux si batch introuvable (404 courant après reset). Pas de toast technique.
      localStorage.removeItem('pulse_batch_id');
      setPhase('invite');
      return null;
    }
  }, []);

  // Initial load : chercher un batch existant (URL > localStorage > dernier actif)
  useEffect(() => {
    if (authLoading || !user || forceInvite) return;
    (async () => {
      // 🆕 Iter388 — Honore le query param ?batch=<id> pour permettre la
      // navigation directe depuis le dashboard (ex: bouton "Suivre la
      // progression" / "Voir le rapport"). Source de vérité prioritaire
      // sur localStorage et sur la liste des batches.
      const urlBatch = searchParams.get('batch');
      if (urlBatch) {
        try { localStorage.setItem('pulse_batch_id', urlBatch); } catch (err) { /* noop */ }
        await refreshBatch(urlBatch);
        return;
      }
      const saved = localStorage.getItem('pulse_batch_id');
      if (saved) {
        await refreshBatch(saved);
        return;
      }
      try {
        const { data } = await axios.get(
          `${API}/team-pulse/batches`, { headers: authHeaders() },
        );
        const list = (data.batches || []).filter(
          (b) => b.batch?.status === 'active' || b.batch?.status === 'completed',
        );
        if (list.length === 0) {
          setPhase('invite');
        } else {
          const latest = list[0].batch;
          localStorage.setItem('pulse_batch_id', latest.id);
          await refreshBatch(latest.id);
        }
      } catch {
        setPhase('invite');
      }
    })();
  }, [authLoading, user, forceInvite, refreshBatch, searchParams]);

  const handleBatchCreated = useCallback(async (bid) => {
    localStorage.setItem('pulse_batch_id', bid);
    setForceInvite(false);
    await refreshBatch(bid);
  }, [refreshBatch]);

  const handleRefresh = useCallback(async () => {
    if (batch?.id) await refreshBatch(batch.id);
  }, [batch, refreshBatch]);

  const handleGenerate = useCallback(async () => {
    if (!batch?.id) return;
    setGenerating(true);
    try {
      const { data } = await axios.post(
        `${API}/team-pulse/batches/${batch.id}/generate-report`, null,
        { headers: authHeaders() },
      );
      setReport(data.report);
      setBatch(data.batch);
      setPhase('teaser');
      toast.success('Rapport prêt.');
      // 🎯 Iter257 C1 — pack pré-calculé backend (source de vérité pricing)
      if (data.batch?.pack_snapshot) setPack(data.batch.pack_snapshot);
    } catch (err) {
      toast.error(friendlyError(err, 'Génération impossible pour le moment.'));
    } finally {
      setGenerating(false);
    }
  }, [batch]);

  const handleUnlock = useCallback(async () => {
    if (!batch?.id) return;
    setUnlocking(true);
    try {
      const { data } = await axios.post(
        `${API}/team-pulse/batches/${batch.id}/checkout`,
        { origin_url: window.location.origin },
        { headers: authHeaders() },
      );
      if (data?.pack) setPack(data.pack);
      // 🎁 Iter336 — FREE FLOW PULSE : si l'admin a configuré le pack à 0 €,
      //   le backend court-circuite Stripe et nous renvoie free_access:true.
      if (data?.free_access) {
        toast.success('🎁 Accès offert activé — votre rapport est disponible', { duration: 4000 });
        setTimeout(() => {
          window.location.href = data.redirect_url || `/team-pulse?free=1&batch_id=${batch.id}`;
        }, 600);
        return;
      }
      // Redirection Stripe
      if (data?.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }
      toast.error('Paiement indisponible pour le moment.');
    } catch (err) {
      toast.error(friendlyError(err, 'Paiement impossible pour le moment.'));
    } finally {
      setUnlocking(false);
    }
  }, [batch]);

  // 🔁 Iter280 — Réconciliation manuelle (filet UX quand l'utilisateur a
  // perdu les query params Stripe : onglet fermé, navigation directe, etc.).
  // On n'exige pas de session_id côté front : le backend retombe sur
  // `batch.stripe_session_id` persisté au moment du checkout.
  const handleManualReconcile = useCallback(async () => {
    if (!batch?.id) return;
    setUnlocking(true);
    toast.message('Vérification du paiement Stripe…');
    try {
      const { data } = await axios.post(
        `${API}/team-pulse/batches/${batch.id}/reconcile-payment`,
        {},
        { headers: authHeaders() },
      );
      if (data?.report_paid) {
        toast.success('Paiement confirmé. Déverrouillage du rapport…');
        const res = await refreshBatch(batch.id);
        if (res?.batch?.report_paid) {
          setPhase('report');
        }
      } else {
        toast.message('Aucun paiement confirmé pour ce rapport.');
      }
    } catch (err) {
      const status = err?.response?.status;
      if (status === 402) {
        toast.error('Paiement Stripe non finalisé. Merci de réessayer le paiement.');
      } else if (status === 400) {
        toast.error(friendlyError(err, 'Aucune session de paiement associée. Relancez le paiement.'));
      } else {
        toast.error(friendlyError(err, 'Vérification impossible pour le moment.'));
      }
    } finally {
      setUnlocking(false);
    }
  }, [batch, refreshBatch]);

  const handleDownloadPdf = useCallback(async () => {
    if (!batch?.id) return;
    setDownloadingPdf(true);
    try {
      const res = await axios.get(
        `${API}/team-pulse/batches/${batch.id}/report/pdf`,
        { headers: authHeaders(), responseType: 'blob' },
      );
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `COACHDIA_Pulse_${batch.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF téléchargé.');
    } catch (err) {
      toast.error(friendlyError(err, 'Téléchargement PDF impossible pour le moment.'));
    } finally {
      setDownloadingPdf(false);
    }
  }, [batch]);

  const handleNewCampaign = useCallback(() => {
    localStorage.removeItem('pulse_batch_id');
    setBatch(null);
    setInvitations([]);
    setProgress(null);
    setReport(null);
    setPack(null);
    setForceInvite(true);
    setPhase('invite');
  }, []);

  // 🎯 Phase 4 — Gestion retour Stripe (?pulse_paid=success&batch_id=...&session_id=...)
  useEffect(() => {
    const paidStatus = searchParams.get('pulse_paid');
    const paidBatchId = searchParams.get('batch_id');
    const paidSessionId = searchParams.get('session_id');
    if (paidStatus === 'success' && paidBatchId) {
      localStorage.setItem('pulse_batch_id', paidBatchId);
      toast.success('Paiement confirmé. Déverrouillage du rapport…');

      // 🔁 Iter259 — Réconciliation directe via Stripe SDK (source de vérité UX).
      // Zéro dépendance webhook : on débloque immédiatement même si le webhook
      // tarde ou ne vient jamais (preview env, URL désynchronisée, etc.).
      const reconcileAndRefresh = async () => {
        if (paidSessionId) {
          try {
            await axios.post(
              `${API}/team-pulse/batches/${paidBatchId}/reconcile-payment`,
              { session_id: paidSessionId },
              { headers: authHeaders() },
            );
          } catch (err) {
            // Webhook a peut-être été plus rapide → on continue sur le retry loop
            const status = err?.response?.status;
            if (status && status !== 409) {
              // 402 = pas encore payé côté Stripe (cas rare, laisser retry) / autre = log seul
              console.warn('[PULSE] reconcile failed', status, err?.response?.data);
            }
          }
        }
        // Retry loop ultime (filet de sécurité + propage l'update UI)
        let attempts = 0;
        const tick = async () => {
          attempts += 1;
          const res = await refreshBatch(paidBatchId);
          if (res?.batch?.report_paid || attempts >= 5) {
            setSearchParams({}, { replace: true });
            return;
          }
          setTimeout(tick, 1500);
        };
        tick();
      };
      reconcileAndRefresh();
    } else if (paidStatus === 'cancelled') {
      toast.message('Paiement annulé.');
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams, refreshBatch]);

  if (authLoading || phase === 'loading') {
    return (
      <div className="min-h-screen bg-[#F5F1EA] flex items-center justify-center" data-testid="pulse-loading">
        <Loader2 className="h-6 w-6 animate-spin text-[#92710A]" />
      </div>
    );
  }

  return (
    <>
      {/* Nav sticky */}
      <div className="sticky top-0 z-40 bg-[#F5F1EA]/95 backdrop-blur border-b border-[#EDE4D0]">
        <div className="max-w-5xl mx-auto px-6 h-14 flex items-center justify-between">
          <BackButton className="text-[#1F1A12]" />
          <button
            onClick={() => navigate('/training')}
            className="text-xs text-[#8A7A5E] hover:text-[#1F1A12] inline-flex items-center gap-1.5"
            data-testid="pulse-home-btn"
          >
            <Home className="h-3.5 w-3.5" /> Tableau de bord
          </button>
        </div>
      </div>

      {phase === 'invite' && <InviteScreen onBatchCreated={handleBatchCreated} />}
      {phase === 'dashboard' && batch && (
        <DashboardScreen
          batch={batch}
          invitations={invitations}
          progress={progress}
          onRefresh={handleRefresh}
          onGenerate={handleGenerate}
          generating={generating}
          onNew={handleNewCampaign}
          onViewTeaser={() => setPhase('teaser')}
          onViewReport={() => setPhase('report')}
        />
      )}
      {phase === 'teaser' && batch && (
        <TeaserScreen
          batch={batch} report={report}
          onUnlock={handleUnlock} unlocking={unlocking}
          pack={pack || batch.pack_snapshot}
          onBackToDashboard={() => setPhase('dashboard')}
          onManualReconcile={handleManualReconcile}
        />
      )}
      {phase === 'report' && batch && (
        <PulseReportView
          batch={batch}
          report={report}
          onBackToDashboard={() => setPhase('dashboard')}
          onDownloadPdf={handleDownloadPdf}
          downloadingPdf={downloadingPdf}
        />
      )}
    </>
  );
}
